/**
 * /simulation - what will run, how it runs, and what has run.
 *
 * Left: the selected scenario, read back the way the solver will see it.
 * Centre: the solver chain, lit from the run's own progress events.
 * Right: every run, sortable; a completed row opens in Inundation.
 *
 * The chain is driven by what the backend actually reports. The pipeline emits
 * no event between "starting" and the first 2-D solver frame, so steps 1-4 light
 * together; the solver frames drive step 5; derivation and writing happen after
 * the last frame and before "complete".
 */

import { RunRecord } from '../components/RunRecord'
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, ChevronUp, Play, Square, X } from 'lucide-react'

import { api, ApiError, eventsUrl } from '../api/client'
import type { Run, RunEvent, RunStatus, Scenario } from '../api/types'
import { useAppStore } from '../store/useAppStore'

interface SitesPayload {
  run_site: string
  sites: { id: string; display_name: string; runnable?: boolean }[]
}

type ScenarioRow = Scenario & { inflow_hydrograph_key?: string | null }

const EASE = 'duration-200 ease-[cubic-bezier(0.2,0,0,1)]'
const HAIRLINE = 'border-b border-[rgb(38_44_54_/_60%)]'

const SOLVER_LABEL: Record<string, string> = {
  quickflood: 'QuickFlood',
  delft3d: 'Delft3D',
  dualsphysics: 'DualSPHysics',
  hecras: 'HEC-RAS',
}

const STATUS_LABEL: Record<RunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

const STATUS_COLOUR: Record<RunStatus, string> = {
  queued: 'var(--ink-text-dim)',
  running: 'var(--ink-accent)',
  done: 'var(--ink-ok)',
  failed: 'var(--ink-alert)',
  cancelled: 'var(--ink-warn)',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function fmt(value: number, digits = 0): string {
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

function titleCase(code: string): string {
  const text = code.replace(/_/g, ' ').toLowerCase()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Run clock, mm:ss or h:mm:ss. */
function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  const h = Math.floor(s / 3600)
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/** The backend stores naive UTC; show it in the viewer's local time. */
function stamp(iso: string): { date: string; time: string } | null {
  const zoned = /Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`
  const d = new Date(zoned)
  if (Number.isNaN(d.getTime())) return null
  return {
    date: `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]}`,
    time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The history names a scenario, never an identifier. A few older scenarios were
 * saved with their own id as the name; those read back from their own geometry.
 */
function scenarioName(s: Scenario): string {
  if (!UUID_RE.test(s.name)) return s.name
  const size = s.breach_width_m ? `${fmt(s.breach_width_m)} × ${fmt(s.breach_depth_m, 1)} m` : `${fmt(s.breach_depth_m, 1)} m deep`
  return `${titleCase(s.failure_mode)}, ${size} breach at ${fmt(s.reservoir_level_m, 2)} m`
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message
  return error instanceof Error ? error.message : String(error)
}

// --- live run ----------------------------------------------------------------

/** 0 idle, 1 pre-solver (steps 1-4), 2 2-D solver (step 5), 3 derive and write (6-7). */
type Phase = 0 | 1 | 2 | 3

interface Live {
  event: RunEvent | null
  receivedAt: number
  phase: Phase
  /** Run-clock seconds by which each phase was observed complete. */
  preDoneAt: number | null
  solverDoneAt: number | null
  doneAt: number | null
}

const IDLE: Live = {
  event: null,
  receivedAt: 0,
  phase: 0,
  preDoneAt: null,
  solverDoneAt: null,
  doneAt: null,
}

function isSolverMessage(message: string | null): boolean {
  return message !== null && (message.startsWith('t = ') || message.startsWith('spin-up'))
}

function isTerminal(status: RunStatus | undefined | null): boolean {
  return status === 'done' || status === 'failed' || status === 'cancelled'
}

function useLiveRun(runId: string | null): Live & { status: RunStatus | null } {
  const queryClient = useQueryClient()
  const [live, setLive] = useState<Live>(IDLE)

  // Backstop: EventSource reconnects silently, and a run that finished during a
  // reconnect would otherwise never show as done.
  const backstop = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.run(runId as string),
    enabled: runId !== null,
    refetchInterval: (query) => (isTerminal(query.state.data?.status) ? false : 3000),
  })

  useEffect(() => {
    setLive(IDLE)
    if (runId === null) return
    const source = new EventSource(eventsUrl(runId))

    const onProgress = (message: MessageEvent<string>) => {
      const event = JSON.parse(message.data) as RunEvent
      const at = event.elapsed_s ?? 0
      setLive((previous) => {
        const next: Live = { ...previous, event, receivedAt: Date.now() }
        if (event.status === 'running') {
          if (isSolverMessage(event.message)) {
            if (previous.phase < 2) {
              next.phase = 2
              next.preDoneAt = at
            }
            if (event.progress_pct >= 100 && next.solverDoneAt === null) {
              next.phase = 3
              next.solverDoneAt = at
            }
          } else if (previous.phase === 0) {
            next.phase = 1
          }
        }
        if (event.status === 'done') {
          next.phase = 3
          next.preDoneAt = previous.preDoneAt ?? at
          next.solverDoneAt = previous.solverDoneAt ?? at
          next.doneAt = at
        }
        return next
      })
      if (isTerminal(event.status)) {
        source.close()
        void queryClient.invalidateQueries({ queryKey: ['runs'] })
        void queryClient.invalidateQueries({ queryKey: ['run', runId] })
      }
    }

    source.addEventListener('progress', onProgress as EventListener)
    return () => {
      source.removeEventListener('progress', onProgress as EventListener)
      source.close()
    }
  }, [runId, queryClient])

  const status = live.event?.status ?? backstop.data?.status ?? null
  return { ...live, status }
}

// --- the solver chain --------------------------------------------------------

type StepState = 'idle' | 'active' | 'done' | 'failed' | 'cancelled'

const STEP_PHASE: Phase[] = [1, 1, 1, 1, 2, 3, 3]

function stepState(index: number, live: Live, status: RunStatus | null): StepState {
  if (status === null || status === 'queued') return 'idle'
  if (status === 'done') return 'done'
  const phase = STEP_PHASE[index]
  const current = live.phase === 0 ? 1 : live.phase
  if (phase < current) return 'done'
  if (phase > current) return 'idle'
  if (status === 'failed') return 'failed'
  if (status === 'cancelled') return 'cancelled'
  return 'active'
}

function stepDoneAt(index: number, live: Live): number | null {
  const phase = STEP_PHASE[index]
  if (phase === 1) return live.preDoneAt
  if (phase === 2) return live.solverDoneAt
  return live.doneAt
}

interface StepSpec {
  title: string
  method: string
  detail?: string | null
  highlight?: boolean
}

function StepMarker({ n, state, highlight }: { n: number; state: StepState; highlight?: boolean }) {
  const base = `flex h-8 w-8 shrink-0 items-center justify-center rounded border text-14 font-semibold transition-colors ${EASE}`
  if (state === 'done')
    return (
      <span className={base} style={{ borderColor: 'var(--ink-ok)', color: 'var(--ink-ok)' }}>
        <Check className="h-4 w-4" />
      </span>
    )
  if (state === 'failed' || state === 'cancelled')
    return (
      <span className={base} style={{ borderColor: STATUS_COLOUR[state], color: STATUS_COLOUR[state] }}>
        <X className="h-4 w-4" />
      </span>
    )
  return (
    <span
      className={`${base} font-mono`}
      style={
        state === 'active'
          ? { background: 'var(--ink-accent)', borderColor: 'var(--ink-accent)', color: 'var(--ink-bg)' }
          : {
              borderColor: highlight ? 'var(--ink-accent)' : 'var(--ink-border)',
              color: highlight ? 'var(--ink-accent)' : 'var(--ink-text-dim)',
            }
      }
    >
      {n}
    </span>
  )
}

function Step({
  n,
  spec,
  state,
  last,
  time,
  children,
}: {
  n: number
  spec: StepSpec
  state: StepState
  last: boolean
  time: string | null
  children?: ReactNode
}) {
  const border =
    state === 'active'
      ? 'var(--ink-accent)'
      : state === 'failed' || state === 'cancelled'
        ? STATUS_COLOUR[state]
        : spec.highlight
          ? 'var(--ink-accent-dim)'
          : 'var(--ink-border)'
  return (
    <li className="flex gap-3" data-step={n} data-state={state}>
      <div className="flex flex-col items-center">
        <StepMarker n={n} state={state} highlight={spec.highlight} />
        {!last && (
          <span
            className={`my-1 w-px flex-1 transition-colors ${EASE}`}
            style={{ background: state === 'done' ? 'var(--ink-ok)' : 'var(--ink-border)' }}
          />
        )}
      </div>
      <div
        className={`mb-2 min-w-0 flex-1 rounded-lg border px-3 py-2 transition-colors ${EASE}`}
        style={{
          borderColor: border,
          borderLeftWidth: spec.highlight ? 3 : 1,
          background:
            state === 'active' || spec.highlight ? 'var(--ink-surface-raised)' : 'var(--ink-surface)',
        }}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-14 font-medium leading-snug text-[var(--ink-text)]">
            {spec.title}
          </span>
          {time && (
            <span className="shrink-0 font-mono text-14 tabular-nums text-[var(--ink-text-dim)]">
              {time}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-14 leading-snug text-[var(--ink-text-dim)]">{spec.method}</div>
        {spec.detail && (
          <div className="mt-1 font-mono text-14 tabular-nums text-[var(--ink-text)]">{spec.detail}</div>
        )}
        {children}
      </div>
    </li>
  )
}

// --- run history -------------------------------------------------------------

type SortKey = 'scenario' | 'solver' | 'grid' | 'duration' | 'status' | 'completed'

/** Shares of the panel. The table is laid out fixed, so no column is ever cut off. */
const COLUMNS: { key: SortKey; label: ReactNode; numeric: boolean; width: string }[] = [
  { key: 'scenario', label: 'Scenario', numeric: false, width: '23.5%' },
  { key: 'solver', label: 'Solver', numeric: false, width: '16.5%' },
  { key: 'grid', label: <>Grid <span className="normal-case">(m)</span></>, numeric: true, width: '13%' },
  { key: 'duration', label: <>Run <span className="normal-case">(min)</span></>, numeric: true, width: '15%' },
  { key: 'status', label: 'Status', numeric: false, width: '11.5%' },
  { key: 'completed', label: 'Completed', numeric: true, width: '20.5%' },
]

function sortValue(run: Run, key: SortKey, names: Map<number, string>): string | number | null {
  switch (key) {
    case 'scenario':
      return (names.get(run.scenario_id) ?? '').toLowerCase()
    case 'solver':
      return SOLVER_LABEL[run.solver] ?? run.solver
    case 'grid':
      return run.grid_resolution_m
    case 'duration':
      return run.wall_clock_s
    case 'status':
      return STATUS_LABEL[run.status]
    case 'completed':
      // A run still going has not completed; it sorts as the most recent.
      return run.finished_at ?? (isTerminal(run.status) ? null : '9999')
  }
}

function RunHistory({ runs, names }: { runs: Run[]; names: Map<number, string> }) {
  const navigate = useNavigate()
  const setSelectedRunId = useAppStore((s) => s.setSelectedRunId)
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'completed', desc: true })

  const sorted = useMemo(() => {
    const rows = [...runs]
    rows.sort((a, b) => {
      const va = sortValue(a, sort.key, names)
      const vb = sortValue(b, sort.key, names)
      if (va === vb) return 0
      if (va === null) return 1
      if (vb === null) return -1
      const order = va < vb ? -1 : 1
      return sort.desc ? -order : order
    })
    return rows
  }, [runs, sort, names])

  const open = (run: Run) => {
    if (run.status !== 'done') return
    setSelectedRunId(run.id)
    navigate('/results')
  }

  return (
    <table
      className="data-table table-fixed [&_td]:!px-1.5 [&_td]:!py-2 [&_td]:align-top [&_th]:!px-1.5"
      data-testid="run-history"
    >
      <colgroup>
        {COLUMNS.map((column) => (
          <col key={column.key} style={{ width: column.width }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {COLUMNS.map((column) => {
            const active = sort.key === column.key
            return (
              <th
                key={column.key}
                aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}
                className={column.numeric ? 'text-right' : 'text-left'}
              >
                <button
                  type="button"
                  onClick={() =>
                    setSort((previous) =>
                      previous.key === column.key
                        ? { key: column.key, desc: !previous.desc }
                        : { key: column.key, desc: column.key === 'completed' },
                    )
                  }
                  className={`inline-flex items-center gap-1 uppercase tracking-[0.08em] transition-colors ${EASE} ${
                    active ? 'text-[var(--ink-accent)]' : 'text-[var(--ink-text-dim)] hover:text-[var(--ink-text)]'
                  }`}
                >
                  {column.label}
                  {active &&
                    (sort.desc ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />)}
                </button>
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {sorted.map((run) => {
          const clickable = run.status === 'done'
          const when = run.finished_at ? stamp(run.finished_at) : null
          return (
            <tr
              key={run.id}
              onClick={() => open(run)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') open(run)
              }}
              tabIndex={clickable ? 0 : -1}
              title={run.error_message ?? undefined}
              className={clickable ? 'cursor-pointer focus-visible:outline-none [&:focus-visible_td]:bg-[rgb(76_195_199_/_8%)]' : ''}
            >
              <td className="leading-snug">{names.get(run.scenario_id) ?? ''}</td>
              <td className="whitespace-nowrap text-[var(--ink-text-dim)]">
                {SOLVER_LABEL[run.solver] ?? run.solver}
              </td>
              <td className="num">{run.grid_resolution_m != null ? fmt(run.grid_resolution_m) : ''}</td>
              <td className="num">{run.wall_clock_s != null ? fmt(run.wall_clock_s / 60, 1) : ''}</td>
              <td className="whitespace-nowrap" style={{ color: STATUS_COLOUR[run.status] }}>
                {STATUS_LABEL[run.status]}
              </td>
              <td className="num whitespace-nowrap text-[var(--ink-text-dim)]">
                {when && (
                  <>
                    <span className="block min-[1700px]:inline">{when.date}</span>
                    <span className="block min-[1700px]:ml-2 min-[1700px]:inline">{when.time}</span>
                  </>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// --- the scenario summary ----------------------------------------------------

function Row({ label, value, unit }: { label: string; value: ReactNode; unit?: string }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 py-2 ${HAIRLINE}`}>
      <dt className="text-14 text-[var(--ink-text-dim)]">{label}</dt>
      <dd className="text-right text-14 text-[var(--ink-text)]">
        {value}
        {unit && <span className="ml-1.5 text-14 text-[var(--ink-text-dim)]">{unit}</span>}
      </dd>
    </div>
  )
}

function Figure({ label, value, unit, note }: { label: string; value: string; unit: string; note?: ReactNode }) {
  return (
    <div className="mt-5">
      <div className="section-label">{label}</div>
      <div className="key-figure mt-1">
        {value}
        <span className="ml-2 font-sans text-14 font-normal text-[var(--ink-text-dim)]">{unit}</span>
      </div>
      {note && <div className="mt-1.5 text-14 text-[var(--ink-text-dim)]">{note}</div>}
    </div>
  )
}

const mono = (value: string) => <span className="font-mono tabular-nums">{value}</span>

// --- page --------------------------------------------------------------------

export default function Simulation() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const activeScenarioId = useAppStore((s) => s.activeScenarioId)
  const setActiveScenarioId = useAppStore((s) => s.setActiveScenarioId)
  const setSelectedRunId = useAppStore((s) => s.setSelectedRunId)
  const activeSiteId = useAppStore((s) => s.activeSiteId)

  const scenarios = useQuery({ queryKey: ['scenarios'], queryFn: api.scenarios })
  const runs = useQuery({ queryKey: ['runs'], queryFn: api.runs, refetchInterval: 5000 })
  const sites = useQuery<SitesPayload>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })
  const cases = useQuery<{ case_id: string; label: string; site_id: string; hazard: string }[]>({
    queryKey: ['hecras-cases'],
    queryFn: () => api.raw('/hecras/cases'),
    staleTime: Infinity,
  })

  const names = useMemo(
    () => new Map((scenarios.data ?? []).map((s) => [s.id, scenarioName(s)])),
    [scenarios.data],
  )

  // Scenarios QuickFlood runs on this site's terrain: on the run site, and not a
  // container for another solver's output (every run of it by another solver).
  const runnable = useMemo(() => {
    if (!scenarios.data || !runs.data || !sites.data) return null
    const runSite = sites.data.run_site
    const solvers = new Map<number, Set<string>>()
    for (const run of runs.data) {
      const set = solvers.get(run.scenario_id) ?? new Set<string>()
      set.add(run.solver)
      solvers.set(run.scenario_id, set)
    }
    return (scenarios.data as ScenarioRow[]).filter((s) => {
      // A scenario that names no site predates Scenario.site_id and is of the run site.
      if ((s.site_id ?? runSite) !== activeSiteId) return false
      const used = solvers.get(s.id)
      return !used || used.has('quickflood')
    })
  }, [scenarios.data, runs.data, sites.data, activeSiteId])

  const scenario =
    runnable?.find((s) => s.id === activeScenarioId) ?? runnable?.[0] ?? undefined
  const scenarioId = scenario?.id ?? null

  // Preview numbers match the run only when the built-in breach model drives it
  // with a constant inflow, which is exactly what the preview endpoint takes.
  const previewable =
    scenario !== undefined && !scenario.hydrograph_csv_path && !scenario.inflow_hydrograph_key

  const preview = useQuery({
    queryKey: ['breach-preview', scenario?.id],
    enabled: previewable,
    staleTime: Infinity,
    queryFn: () => {
      const s = scenario as ScenarioRow
      return api.previewBreach({
        dam_id: s.dam_id,
        reservoir_level_m: s.reservoir_level_m,
        breach_depth_m: s.breach_depth_m,
        failure_mode: s.failure_mode,
        breach_width_m: s.breach_width_m,
        breach_formation_time_s: s.breach_formation_time_s,
        reservoir_inflow_cumecs: s.reservoir_inflow_cumecs,
        duration_hours: s.duration_hours,
      })
    },
  })

  // Median of completed QuickFlood runs at the same grid and simulated duration.
  const estimate = useMemo(() => {
    if (!scenario || !runs.data || !scenarios.data) return null
    const durations = new Map(scenarios.data.map((s) => [s.id, s.duration_hours]))
    const samples = runs.data
      .filter(
        (r) =>
          r.solver === 'quickflood' &&
          r.status === 'done' &&
          r.wall_clock_s != null &&
          r.grid_resolution_m === scenario.grid_resolution_m &&
          durations.get(r.scenario_id) === scenario.duration_hours,
      )
      .map((r) => r.wall_clock_s as number)
      .sort((a, b) => a - b)
    if (samples.length === 0) return null
    const mid = Math.floor(samples.length / 2)
    const median = samples.length % 2 ? samples[mid] : (samples[mid - 1] + samples[mid]) / 2
    return { median, min: samples[0], max: samples[samples.length - 1], n: samples.length }
  }, [scenario, runs.data, scenarios.data])

  // The run the chain is showing: the one started here, or a QuickFlood run already going.
  const [liveRunId, setLiveRunId] = useState<string | null>(null)
  useEffect(() => {
    if (liveRunId !== null || !runs.data) return
    const going = runs.data.find(
      (r) => r.solver === 'quickflood' && (r.status === 'running' || r.status === 'queued'),
    )
    if (going) setLiveRunId(going.id)
  }, [runs.data, liveRunId])

  const live = useLiveRun(liveRunId)
  const liveRun = runs.data?.find((r) => r.id === liveRunId)
  const running = live.status === 'running' || live.status === 'queued'

  useEffect(() => {
    if (live.status === 'done' && liveRunId) setSelectedRunId(liveRunId)
  }, [live.status, liveRunId, setSelectedRunId])

  // A one-second tick for the run clock between events.
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!running) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [running])

  const elapsed =
    live.event?.elapsed_s != null
      ? live.event.elapsed_s + (running ? Math.max(0, (now - live.receivedAt) / 1000) : 0)
      : null

  const start = useMutation({
    mutationFn: () => api.createRun(scenarioId as number, 'quickflood'),
    onSuccess: (run) => {
      setLiveRunId(run.id)
      void queryClient.invalidateQueries({ queryKey: ['runs'] })
    },
  })

  const cancel = useMutation({
    mutationFn: () => api.cancelRun(liveRunId as string),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['runs'] }),
  })

  // --- step text, from the scenario and the breach module's own numbers ------
  const supplied = Boolean(scenario?.hydrograph_csv_path)
  const p = previewable ? preview.data : undefined
  const mechanism = scenario ? titleCase(scenario.mechanism ?? scenario.failure_mode) : ''

  const steps: StepSpec[] = [
    {
      title: 'Load terrain and reservoir curve',
      method: scenario
        ? `DEM resampled to ${fmt(scenario.grid_resolution_m)} m · flow network · storage–elevation curve`
        : 'DEM resample · flow network · storage–elevation curve',
    },
    {
      title: 'Route the inflow flood through the reservoir',
      method: supplied ? 'Supplied outflow hydrograph' : 'Level-pool continuity, explicit forward Euler',
    },
    {
      title: 'Open the breach at the trigger level',
      method: supplied
        ? 'Supplied outflow hydrograph'
        : `${mechanism} from the trigger level, over the formation time · outflow from the breach geometry as it opens`,
      detail:
        scenario && p
          ? `Trigger ${fmt(scenario.reservoir_level_m, 2)} m · invert ${fmt(p.invert_elevation_m, 2)} m · formation ${fmt(p.formation_time_min)} min`
          : null,
      highlight: true,
    },
    {
      title: 'Grow the breach, compute outflow',
      method: supplied
        ? 'Supplied outflow hydrograph'
        : 'Froehlich (2008) geometry · linear growth · weir outflow',
      detail: p
        ? `${fmt(p.width_m)} m wide in ${fmt(p.formation_time_min)} min · peak ${fmt(p.peak_discharge_cumecs)} m³/s`
        : null,
    },
    {
      title: 'Route the flood across the terrain',
      method: '2-D local-inertial shallow water (Bates et al. 2010)',
    },
    {
      title: 'Derive depth, velocity, arrival time, hazard class',
      method: 'Per-cell maxima · arrival at rise threshold · hazard classes 1–4',
    },
    {
      title: 'Write results',
      method: 'result.nc · GeoTIFF layers · outflow hydrograph',
    },
  ]

  const siteName = sites.data?.sites.find(
    (s) => s.id === (scenario?.site_id ?? sites.data?.run_site),
  )?.display_name
  const frames = scenario
    ? Math.floor((scenario.duration_hours * 3600) / scenario.output_interval_s) + 1
    : null

  const hasInputs = runnable !== null && runnable.length > 0
  const siteCases = (cases.data ?? []).filter((c) => c.site_id === activeSiteId)
  const flashCase = siteCases.find((c) => c.hazard === 'FLASH_FLOOD') ?? null
  const siteDone = (runs.data ?? []).filter((r) => r.site_id === activeSiteId && r.status === 'done')
  const handoffRun = live.status === 'done' && liveRunId ? liveRunId : null
  // The reference the Comparison page scores this site's models against (backend/compare/reference.py).
  const comparedRun = handoffRun ?? siteDone.find((r) => r.solver !== 'dualsphysics')?.id ?? null
  const models = useQuery<{ scores: { reference_label: string }[] }>({
    queryKey: ['compare-models', comparedRun],
    queryFn: () => api.raw(`/compare/models?run_id=${encodeURIComponent(comparedRun!)}`),
    enabled: Boolean(comparedRun),
    staleTime: 60_000,
  })
  const reference = models.data?.scores[0] ?? null

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-[var(--ink-bg)] text-[var(--ink-text)]" data-testid="simulation-page">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-4 p-4">
        {/* 1 - inputs */}
        {(runnable === null || hasInputs) && (
          <section className="panel" style={{ padding: 0 }} data-testid="run-inputs">
            <header className="panel-header">
              <h2 className="panel-title">1 · Inputs</h2>
            </header>
            <div className="gap-x-12 p-4 lg:columns-2 [&>*]:break-inside-avoid">
          {runnable === null && <div className="skeleton h-10" />}
          {runnable && runnable.length > 0 && (
            <select
              value={scenarioId ?? ''}
              onChange={(event) => setActiveScenarioId(Number(event.target.value))}
              aria-label="Scenario"
              className="ctl h-10 w-full !text-14"
            >
              {runnable.map((s) => (
                <option key={s.id} value={s.id}>
                  {scenarioName(s)}
                </option>
              ))}
            </select>
          )}

          {scenario && (
            <>
              <dl className="mt-3">
                {siteName && <Row label="Site" value={siteName} />}
                {scenario.hazard_type && <Row label="Hazard" value={titleCase(scenario.hazard_type)} />}
                <Row label="Mechanism" value={mechanism} />
                <Row label="Trigger level" value={mono(fmt(scenario.reservoir_level_m, 2))} unit="m" />
                {scenario.inflow_hydrograph_key ? (
                  <Row label="Inflow flood" value={scenario.inflow_hydrograph_key} />
                ) : (
                  <Row label="Inflow peak" value={mono(fmt(scenario.reservoir_inflow_cumecs))} unit="m³/s" />
                )}
                <Row label="Grid resolution" value={mono(fmt(scenario.grid_resolution_m))} unit="m" />
                <Row
                  label="Simulated duration"
                  value={mono(fmt(scenario.duration_hours, scenario.duration_hours % 1 ? 2 : 0))}
                  unit="h"
                />
              </dl>

              {previewable && (
                <>
                  <div className="section-label mb-1 mt-5">Breach geometry</div>
                  {preview.isPending && (
                    <div className="space-y-2">
                      <div className="skeleton h-6" />
                      <div className="skeleton h-6" />
                      <div className="skeleton h-6" />
                    </div>
                  )}
                  {p && (
                    <dl>
                      <Row
                        label="Width"
                        value={mono(fmt(p.width_m))}
                        unit={p.width_source === 'froehlich' ? 'm · Froehlich' : 'm'}
                      />
                      <Row label="Depth" value={mono(fmt(p.depth_m, 1))} unit="m" />
                      <Row label="Invert" value={mono(fmt(p.invert_elevation_m, 2))} unit="m" />
                      <Row label="Side slope" value={mono(`1 : ${fmt(p.side_slope_z, 1)}`)} unit="V : H" />
                      <Row
                        label="Formation time"
                        value={mono(fmt(p.formation_time_min))}
                        unit={p.formation_time_source === 'froehlich' ? 'min · Froehlich' : 'min'}
                      />
                    </dl>
                  )}
                  {p && <Figure label="Peak breach outflow" value={fmt(p.peak_discharge_cumecs)} unit="m³/s" />}
                </>
              )}

              {estimate && (
                <Figure
                  label="Estimated runtime"
                  value={fmt(estimate.median / 60, 1)}
                  unit="min"
                  note={
                    <>
                      Median of {mono(String(estimate.n))} QuickFlood runs at{' '}
                      {mono(fmt(scenario.grid_resolution_m))} m, {mono(fmt(scenario.duration_hours))} h
                      {estimate.n > 1 && (
                        <>
                          {' '}· range {mono(fmt(estimate.min / 60, 1))}–{mono(fmt(estimate.max / 60, 1))} min
                        </>
                      )}
                    </>
                  }
                />
              )}

              <div className="section-label mb-1 mt-5">The run produces</div>
              <ul className="text-14 leading-snug">
                <li className={`py-1.5 ${HAIRLINE}`}>Max depth, max velocity, depth × velocity</li>
                <li className={`py-1.5 ${HAIRLINE}`}>Arrival time and flood duration</li>
                <li className={`py-1.5 ${HAIRLINE}`}>Hazard class 1–4 per cell</li>
                <li className={`py-1.5 ${HAIRLINE}`}>Breach outflow hydrograph</li>
                {frames !== null && (
                  <li className="py-1.5">
                    {mono(fmt(frames))} depth and velocity frames, every{' '}
                    {mono(fmt(scenario.output_interval_s / 60))} min
                  </li>
                )}
              </ul>
            </>
          )}
            </div>
          </section>
        )}

        {/* 2 - the solver chain */}
        {hasInputs && (
          <section className="panel" style={{ padding: 0 }} data-testid="solver-chain">
            <header className="panel-header">
              <h2 className="panel-title">2 · Solver chain</h2>
              {liveRun && (
                <span className="truncate text-14 text-[var(--ink-text-dim)]" title={names.get(liveRun.scenario_id)}>
                  {names.get(liveRun.scenario_id)}
                </span>
              )}
            </header>
        <ol className="grid gap-x-6 p-4 xl:grid-cols-2">
          {steps.map((spec, index) => {
            const state = stepState(index, live, live.status)
            const at = stepDoneAt(index, live)
            const time =
              state === 'done' && at !== null
                ? `T+${clock(at)}`
                : state === 'active' && elapsed !== null
                  ? `T+${clock(elapsed)}`
                  : null
            return (
              <Step
                key={spec.title}
                n={index + 1}
                spec={spec}
                state={state}
                last={index === steps.length - 1}
                time={time}
              >
                {(state === 'failed' || state === 'cancelled') && liveRun?.error_message && (
                  <div className="mt-1 text-14 leading-snug" style={{ color: 'var(--ink-alert)' }}>
                    {liveRun.error_message}
                  </div>
                )}
              </Step>
            )
          })}
        </ol>
            <div className="border-t border-[var(--ink-border)] p-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => start.mutate()}
              disabled={scenarioId === null || running || start.isPending}
              className={`inline-flex h-12 flex-1 items-center justify-center gap-2 rounded text-16 font-semibold transition-colors ${EASE} enabled:bg-[var(--ink-accent)] enabled:text-[var(--ink-bg)] enabled:hover:bg-[var(--ink-accent)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--ink-border)] disabled:bg-[var(--ink-surface-raised)] disabled:text-[var(--ink-text-dim)]`}
              data-testid="run-button"
            >
              <Play className="h-4 w-4" />
              {running
                ? live.status === 'queued'
                  ? 'Queued'
                  : elapsed !== null
                    ? `Running · ${clock(elapsed)}`
                    : 'Running'
                : 'Run QuickFlood'}
            </button>
            {running && (
              <button
                type="button"
                onClick={() => cancel.mutate()}
                disabled={cancel.isPending}
                className="ctl inline-flex h-12 items-center gap-2 !px-4 !text-14"
              >
                <Square className="h-3.5 w-3.5" />
                Cancel
              </button>
            )}
          </div>
          {start.isError && (
            <p className="mt-2 text-14 leading-snug" style={{ color: 'var(--ink-alert)' }}>
              {errorText(start.error)}
            </p>
          )}
            </div>
          </section>
        )}

        {/* 3 - live progress */}
        {liveRun && (running || live.status === 'done' || live.status === 'failed' || live.status === 'cancelled') && (
          <section className="panel" style={{ padding: 0 }} data-testid="live-progress">
            <header className="panel-header">
              <h2 className="panel-title">3 · Live progress</h2>
              <span className="font-mono text-14 tabular-nums text-[var(--ink-text-dim)]">
                {live.status}
                {elapsed !== null && ` · ${clock(elapsed)}`}
              </span>
            </header>
            <div className="p-4">
              {live.event && (
                <>
                  <div className="h-1 overflow-hidden rounded bg-[var(--ink-border)]">
                    <div
                      className={`h-full bg-[var(--ink-accent)] transition-[width] ${EASE}`}
                      style={{ width: `${Math.min(100, live.event.progress_pct).toFixed(1)}%` }}
                    />
                  </div>
                  <div className="mt-2 font-mono text-14 tabular-nums text-[var(--ink-text)]">
                    {fmt(live.event.progress_pct)}%{live.event.message ? ` · ${live.event.message}` : ''}
                  </div>
                </>
              )}
              {liveRun.error_message && (
                <div className="mt-2 text-14 leading-snug" style={{ color: 'var(--ink-alert)' }}>
                  {liveRun.error_message}
                </div>
              )}
            </div>
          </section>
        )}

        {/* 4 - results handoff */}
        {(handoffRun || siteDone.length > 0) && (
          <section className="panel" style={{ padding: 0 }} data-testid="results-handoff">
            <header className="panel-header">
              <h2 className="panel-title">4 · Results</h2>
            </header>
            <div className="grid gap-4 p-4 md:grid-cols-3">
              <div>
                <div className="section-label mb-2">Open</div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRunId(handoffRun ?? siteDone[0].id)
                    navigate('/results')
                  }}
                  className="ctl inline-flex h-10 items-center !px-4 !text-14"
                >
                  {handoffRun ? 'Open this run in Inundation' : 'Open in Inundation'}
                </button>
                <button type="button" onClick={() => navigate('/compare-models')} className="ctl ml-2 inline-flex h-10 items-center !px-4 !text-14">
                  Compare models
                </button>
              </div>
              {reference && (
                <div>
                  <div className="section-label mb-2">Selected reference</div>
                  <div className="text-14">{reference.reference_label}</div>
                </div>
              )}
              {flashCase && (
                <div>
                  <div className="section-label mb-2">Flash-flood parameters</div>
                  <button type="button" onClick={() => navigate('/setup')} className="text-left text-14 underline decoration-[var(--ink-border)] underline-offset-4">
                    {flashCase.label}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* 5 - the completed run as recorded: inputs, chain, provenance, timings, mass balance (every site) */}
        {siteDone.length > 0 && <RunRecord runs={siteDone.filter((r) => r.solver !== 'dualsphysics')} />}

        {/* run history */}
        <section className="panel" style={{ padding: 0 }}>
          <header className="panel-header">
            <h2 className="panel-title">Run history</h2>
            {runs.data && (
              <span className="font-mono text-14 tabular-nums text-[var(--ink-text-dim)]">
                {runs.data.filter((r) => r.site_id === activeSiteId).length} runs
              </span>
            )}
          </header>
          <div className="max-h-[480px] overflow-y-auto">
            {runs.isPending && (
              <div className="space-y-2 p-4">
                {Array.from({ length: 8 }, (_, i) => (
                  <div key={i} className="skeleton h-8" />
                ))}
              </div>
            )}
            {runs.data && <RunHistory runs={runs.data.filter((r) => r.site_id === activeSiteId)} names={names} />}
          </div>
        </section>
      </div>
    </div>
  )
}
